import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  useFetchVendors,
} from "../../hooks/toolbox-hooks";
import { ArrowBack, ExpandMore } from "@mui/icons-material";
import ChildCrudPanel from "./Panels/ChildCrudPanel";
import ResourceRelationPanel from "./Panels/ResourceRelationPanel";
import TagManagementSection from "./Common/TagManagementSection";
import ImageUploadSelectControl from "./Panels/ImageUploadSelectControl";
import {
  useAddProductMutation,
  useFetchCiscoProducts,
  useFetchSingleProduct,
  useUpdateProductMutation
} from "../../hooks/product-hooks";
import SnackableCourseButton from './SnackableCourse/SnackableCourseButton';
import SnackableCourseManager from './SnackableCourse/SnackableCourseManager';
import { CacheKey } from "../../types/cache-keys";
import { validId } from "../../hooks/base-hooks";
import { Product } from "../../types/product";
import { useNavigate, useParams } from "react-router-dom";
import { ADMIN_URL_DATA_MANAGMENT } from "./PageStructure/admin-values";
import { useAdminPermissions } from "./Hooks/useAdminPermissions";
import NavHistoryTracker from "../../navigation/NavHistoryTracker";
import AdminConfirmDialog from "./Common/AdminConfirmDialog";
import {
  APPLIANCE_OFFER_TYPES,
  SAAS_OFFER_TYPES,
  normalizeOfferType,
} from "../../support/productRelationshipUtils";
import { buildApiUrl, getApiHeaders } from "../../support/queriesHelpers";
import { VersionScope } from "../../types/snackableCourse";
import {
  VIEW_ALL_VERSION_VALUE,
  buildRecommendedVersionScope,
  getProductVersionsFromProduct,
  getRecommendedVersion,
  getVersionLabel,
  sortVersionsForDisplay,
} from "../../support/versionScopeUtils";

type Props = {
  readOnly?: boolean;
  canCreateChildren?: boolean;
  canUpdateChildren?: boolean;
  canDeleteChildren?: boolean;
};

type ProductForm = Record<string, any> & { name: string; vendor_id?: any };

type SvgIconRecord = Record<string, any> & {
  id?: number | string;
  content?: string | null;
  svg?: string | null;
  svg_content?: string | null;
  image?: string | null;
  image_url?: string | null;
  url?: string | null;
  file?: string | null;
};

const emptyProductForm: ProductForm = {
  name: '',
  description: '',
  vendor_id: '',
  sales_url: '',
  sales_connect_url: '',
  offer_type: '',
  abbreviation: '',
  recommended_version: '',
  release_date: '',
  end_of_life_date: '',
  stc_trial_available: false,
  mssp_service_creation: false,
  svg_icon: null,
};

const nameOnlyColumns = [
  { key: "name", label: "Name" },
];

const productFeatureColumns = [
  { key: "name", label: "Name" },
  { key: "short_description", label: "Description" },
];

const equalHeightGridSx = {
  alignItems: 'stretch',
};

const equalHeightGridItemSx = {
  display: 'flex',
  minWidth: 0,
  '& > *': {
    flex: 1,
    width: '100%',
    minWidth: 0,
  },
  '& .MuiPaper-root': {
    width: '100%',
  },
};

const fullWidthPanelSx = {
  display: 'flex',
  width: '100%',
  minWidth: 0,
  '& > *': {
    flex: 1,
    width: '100%',
    minWidth: 0,
  },
  '& .MuiPaper-root': {
    width: '100%',
  },
};

const normalizeDateOrNull = (value: unknown): string | null => {
  if (value == null) return null;

  const text = String(value).trim();

  if (!text) return null;

  return text.length >= 10 ? text.slice(0, 10) : text;
};

const normalizeDateOrEmpty = (value: unknown): string => {
  return normalizeDateOrNull(value) || '';
};

const normalizeForeignKeyId = (value: unknown): number | string | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : trimmed;
  }

  if (typeof value === 'object' && value !== null && 'id' in value) {
    return normalizeForeignKeyId((value as { id?: unknown }).id);
  }

  return null;
};

const getSvgIconId = (value: unknown): number | string | null => {
  return normalizeForeignKeyId(value);
};

const getNestedSvgIconRecord = (...values: unknown[]): SvgIconRecord | null => {
  for (const value of values) {
    if (typeof value === 'object' && value !== null) {
      const record = value as SvgIconRecord;

      if (
        record.content ||
        record.svg ||
        record.svg_content ||
        record.image ||
        record.image_url ||
        record.url ||
        record.file
      ) {
        return record;
      }
    }
  }

  return null;
};

const extractSvgIconContent = (icon: SvgIconRecord | null | undefined): string | null => {
  if (!icon) return null;

  const candidates = [
    icon.content,
    icon.svg,
    icon.svg_content,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

const extractSvgIconUrl = (icon: SvgIconRecord | null | undefined): string | null => {
  if (!icon) return null;

  const candidates = [
    icon.image_url,
    icon.url,
    icon.image,
    icon.file,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

const getSvgIconSrcFromContent = (content: string | null): string | null => {
  if (!content) return null;

  const trimmed = content.trim();

  if (!trimmed) return null;

  if (
    trimmed.startsWith('data:') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/')
  ) {
    return trimmed;
  }

  return `data:image/svg+xml;utf8,${encodeURIComponent(trimmed)}`;
};

const getSvgIconEndpoint = (iconId: number | string): string => {
  const idText = String(iconId).trim();
  return `db/svg-icon/${encodeURIComponent(idText)}/`;
};

const normalizeSvgIconListResponse = (value: any): SvgIconRecord[] => {
  if (Array.isArray(value)) return value;

  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const fetchSvgIconRecordById = async (
  iconId: number | string,
  signal?: AbortSignal,
): Promise<SvgIconRecord | null> => {
  const idText = String(iconId).trim();

  if (!idText) return null;

  const detailEndpoint = getSvgIconEndpoint(idText);

  const detailResponse = await fetch(buildApiUrl(detailEndpoint), {
    method: 'GET',
    headers: getApiHeaders(null, 'GET', detailEndpoint),
    credentials: 'include',
    signal,
  });

  if (detailResponse.ok) {
    const detail = await detailResponse.json();
    if (detail && typeof detail === 'object') {
      return detail as SvgIconRecord;
    }
  }

  const listResponse = await fetch(buildApiUrl('db/svg-icon/'), {
    method: 'GET',
    headers: getApiHeaders(null, 'GET', 'db/svg-icon/'),
    credentials: 'include',
    signal,
  });

  if (!listResponse.ok) return null;

  const listPayload = await listResponse.json();
  const icons = normalizeSvgIconListResponse(listPayload);

  return icons.find((icon) => String(icon?.id ?? '').trim() === idText) ?? null;
};

const getSvgIconContent = (
  product: any,
  form: ProductForm,
  selectedSvgIconRecord: SvgIconRecord | null,
): string | null => {
  const nestedRecord = getNestedSvgIconRecord(
    selectedSvgIconRecord,
    form?.svg_icon,
    product?.svg_icon,
    product?.svg_icon_detail,
    product?.icon_detail,
  );

  return extractSvgIconContent(nestedRecord);
};

const getSvgIconUrl = (
  product: any,
  form: ProductForm,
  selectedSvgIconRecord: SvgIconRecord | null,
): string | null => {
  const nestedRecord = getNestedSvgIconRecord(
    selectedSvgIconRecord,
    form?.svg_icon,
    product?.svg_icon,
    product?.svg_icon_detail,
    product?.icon_detail,
  );

  return extractSvgIconUrl(nestedRecord);
};

const normalizeProductFormForState = (value: any): ProductForm => {
  return {
    ...emptyProductForm,
    ...value,
    vendor_id: typeof value?.vendor === 'object' ? value.vendor?.id : value?.vendor_id,
    svg_icon: getSvgIconId(value?.svg_icon),
    release_date: normalizeDateOrEmpty(value?.release_date),
    end_of_life_date: normalizeDateOrEmpty(value?.end_of_life_date),
  };
};

const buildProductSavePayload = (
  form: ProductForm,
  productNumericId?: number | null
): Record<string, any> => {
  return {
    ...(productNumericId ? { id: productNumericId } : {}),

    name: form.name ?? '',
    description: form.description || null,
    vendor_id: form.vendor_id,
    sales_url: form.sales_url || null,
    sales_connect_url: form.sales_connect_url || null,
    offer_type: form.offer_type || null,
    abbreviation: form.abbreviation || null,
    recommended_version: form.recommended_version || null,
    release_date: normalizeDateOrNull(form.release_date),
    end_of_life_date: normalizeDateOrNull(form.end_of_life_date),
    stc_trial_available: !!form.stc_trial_available,
    mssp_service_creation: !!form.mssp_service_creation,
    svg_icon: getSvgIconId(form.svg_icon),

    firestarter_pov: form.firestarter_pov || null,
    firestarter_tme: form.firestarter_tme || null,
    firestarter_presentation: form.firestarter_presentation || null,
    firestarter_automation: form.firestarter_automation || null,
    firestarter_feature: form.firestarter_feature || null,
    sf_technology: form.sf_technology || null,
    design_guide: form.design_guide || null,
    pov_guide: form.pov_guide || null,
    data_sheet: form.data_sheet || null,
    ordering_guide: form.ordering_guide || null,
    competitive_resources: form.competitive_resources || null,
    icon: form.icon || null,
  };
};

export default function ProductEditorPage({
                                            readOnly = false,
                                            canCreateChildren = true,
                                            canUpdateChildren = true,
                                            canDeleteChildren = true,
                                          }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const productId: string = id ?? 'new';
  const isNew = productId === 'new';
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [selectedSvgIconRecord, setSelectedSvgIconRecord] = useState<SvgIconRecord | null>(null);

  const productNumericId = isNew ? null : Number(productId);
  const { hasProductScope } = useAdminPermissions();

  const { data: products, isLoading: isLoadingProducts } = useFetchCiscoProducts();
  const { data: singleProduct, isLoading: isLoadingSingleProduct } =
    useFetchSingleProduct(productNumericId, {}, { enabled: !isNew && validId(productNumericId) });

  const product = useMemo(() => {
    if (!isNew && productNumericId) {
      if (isLoadingProducts && isLoadingSingleProduct) return null;
      if (singleProduct) return singleProduct;
      const found = products?.find((s) => s.id === productNumericId);
      return found ?? null;
    }
    return null;
  }, [productNumericId, products, isLoadingProducts, isNew, singleProduct, isLoadingSingleProduct]);

  const productForIcon = product as any;

  const { data: vendors, isLoading: isLoadingVendors } = useFetchVendors();

  const { mutateAsync: addProduct, isPending: isAdding } = useAddProductMutation();
  const { mutateAsync: updateProduct, isPending: isUpdating } = useUpdateProductMutation();

  const [form, setForm] = useState<ProductForm>(emptyProductForm);
  const [initialForm, setInitialForm] = useState<ProductForm>(emptyProductForm);

  /**
   * null means View All Versions.
   * number means selected version only.
   */
  const [selectedCatalogVersionId, setSelectedCatalogVersionId] = useState<number | null>(null);

  const normalizedOfferType = normalizeOfferType(form.offer_type);

  const shouldUseVersionScopedView =
    APPLIANCE_OFFER_TYPES.has(normalizedOfferType);

  const isSaasOffer =
    SAAS_OFFER_TYPES.has(normalizedOfferType);

  const productVersions = useMemo(() => {
    return sortVersionsForDisplay(getProductVersionsFromProduct(product));
  }, [product]);

  const selectedCatalogVersion = useMemo(() => {
    if (!selectedCatalogVersionId) return null;
    return productVersions.find((version) => Number(version?.id) === Number(selectedCatalogVersionId)) ?? null;
  }, [productVersions, selectedCatalogVersionId]);

  const isAllVersionsSelected =
    shouldUseVersionScopedView && selectedCatalogVersionId == null;

  const selectedCatalogVersionLabel = selectedCatalogVersion
    ? `Version ${getVersionLabel(selectedCatalogVersion)}`
    : "All Versions";

  const selectedVersionScope: VersionScope | null = useMemo(() => {
    if (!shouldUseVersionScopedView) {
      return null;
    }

    if (!selectedCatalogVersionId || !selectedCatalogVersion) {
      return null;
    }

    return {
      product_id: productNumericId,
      product_name: form.name || product?.name || null,
      product_version_id: Number(selectedCatalogVersionId),
      product_version_name: getVersionLabel(selectedCatalogVersion),
      doc_version: getVersionLabel(selectedCatalogVersion),
      deployment_scope: normalizedOfferType === "hybrid_product" || normalizedOfferType === "hybriud_product"
        ? "hybrid"
        : "appliance",
      generation_scope: "version",
      version_source: "selected_product_version",
    };
  }, [
    shouldUseVersionScopedView,
    selectedCatalogVersionId,
    selectedCatalogVersion,
    productNumericId,
    form.name,
    product?.name,
    normalizedOfferType,
  ]);

  /**
   * Used for product-level resources and course generation.
   * For version-scoped products, this is intentionally null when View All Versions is selected.
   */
  const effectiveProductResourceScope: VersionScope | null = useMemo(() => {
    if (isAllVersionsSelected) return null;

    return selectedVersionScope || buildRecommendedVersionScope({
      product,
      productId: productNumericId,
      versionScope: null,
    });
  }, [isAllVersionsSelected, selectedVersionScope, product, productNumericId]);

  const shouldShowProductScopedActions =
    !isNew &&
    (
      !shouldUseVersionScopedView ||
      !!selectedVersionScope
    );

  useEffect(() => {
    if (product && !isNew) {
      const nextForm = normalizeProductFormForState(product);
      setForm(nextForm);
      setInitialForm(nextForm);
    }
  }, [product, isNew]);

  useEffect(() => {
    if (!vendors || isLoadingVendors) return;
    const ciscoVendor = vendors?.find((v: any) => v.is_cisco);
    if (ciscoVendor) {
      setForm((prev) => ({ ...prev, vendor_id: ciscoVendor.id }));
      if (isNew) {
        setInitialForm((prev) => ({ ...prev, vendor_id: ciscoVendor.id }));
      }
    }
  }, [vendors, isLoadingVendors, isNew]);

  useEffect(() => {
    const iconId = getSvgIconId(form.svg_icon);

    setSelectedSvgIconRecord((previous) => {
      if (
        previous &&
        iconId != null &&
        String(previous.id ?? '').trim() === String(iconId).trim()
      ) {
        return previous;
      }

      return null;
    });

    if (iconId == null || iconId === '') {
      setSelectedSvgIconRecord(null);
      return;
    }

    const nestedRecord = getNestedSvgIconRecord(
      form?.svg_icon,
      productForIcon?.svg_icon,
      productForIcon?.svg_icon_detail,
      productForIcon?.icon_detail,
    );

    if (
      nestedRecord &&
      (
        extractSvgIconContent(nestedRecord) ||
        extractSvgIconUrl(nestedRecord)
      )
    ) {
      setSelectedSvgIconRecord(nestedRecord);
      return;
    }

    const controller = new AbortController();

    fetchSvgIconRecordById(iconId, controller.signal)
      .then((record) => {
        if (!controller.signal.aborted) {
          setSelectedSvgIconRecord(record);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setSelectedSvgIconRecord(null);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    form.svg_icon,
    productForIcon?.svg_icon,
    productForIcon?.svg_icon_detail,
    productForIcon?.icon_detail,
  ]);

  useEffect(() => {
    if (!shouldUseVersionScopedView) {
      setSelectedCatalogVersionId(null);
      return;
    }

    if (!productVersions.length) {
      setSelectedCatalogVersionId(null);
      return;
    }

    if (
      selectedCatalogVersionId != null &&
      productVersions.some((version: any) => Number(version.id) === Number(selectedCatalogVersionId))
    ) {
      return;
    }
  }, [
    shouldUseVersionScopedView,
    productVersions,
    selectedCatalogVersionId,
  ]);

  const saving = isAdding || isUpdating;
  const loading = isLoadingProducts || isLoadingSingleProduct;

  const isProductDetailLoading =
    !isNew &&
    validId(productNumericId) &&
    isLoadingSingleProduct;

  const isVersionStateLoading =
    !isNew &&
    shouldUseVersionScopedView &&
    (
      isProductDetailLoading ||
      (!product && loading)
    );

  const shouldShowVersionLoadingState =
    !isNew &&
    shouldUseVersionScopedView &&
    isVersionStateLoading;

  const shouldShowNoVersionsWarning =
    !isNew &&
    shouldUseVersionScopedView &&
    !isVersionStateLoading &&
    !!product &&
    productVersions.length === 0;

  const hasUnsavedChanges = useMemo(() => {
    const currentPayload = buildProductSavePayload(form, productNumericId);
    const initialPayload = buildProductSavePayload(initialForm, productNumericId);
    return JSON.stringify(currentPayload) !== JSON.stringify(initialPayload);
  }, [form, initialForm, productNumericId]);

  const handleSave = async () => {
    if (readOnly || !hasUnsavedChanges) return;

    const payload = buildProductSavePayload(form, productNumericId);

    if (isNew) {
      const created: Product = await addProduct(payload as any);
      if (created?.id) {
        navigate(`${ADMIN_URL_DATA_MANAGMENT}/products/${created.id}`);
      }
    } else if (productNumericId) {
      await updateProduct(payload as any);

      const nextInitialForm = normalizeProductFormForState({
        ...form,
        release_date: normalizeDateOrEmpty(form.release_date),
        end_of_life_date: normalizeDateOrEmpty(form.end_of_life_date),
        svg_icon: getSvgIconId(form.svg_icon),
      });

      setForm(nextInitialForm);
      setInitialForm(nextInitialForm);
    }
  };

  const resetAndClose = () => {
    setConfirmCloseOpen(false);
    navigate(`${ADMIN_URL_DATA_MANAGMENT}/products`);
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleUseRecommendedVersion = () => {
    const recommended = getRecommendedVersion(productVersions, form.recommended_version);
    if (recommended?.id != null) {
      setSelectedCatalogVersionId(Number(recommended.id));
    }
  };

  const renderProductTitleIcon = () => {
    const svgContent = getSvgIconContent(productForIcon, form, selectedSvgIconRecord);
    const svgContentSrc = getSvgIconSrcFromContent(svgContent);
    const svgUrl = getSvgIconUrl(productForIcon, form, selectedSvgIconRecord);
    const imageSrc = svgContentSrc || svgUrl;

    if (imageSrc) {
      return (
        <Box
          component="img"
          src={imageSrc}
          alt={`${form.name || product?.name || 'Product'} icon`}
          sx={{
            width: 28,
            height: 28,
            objectFit: 'contain',
            flexShrink: 0,
          }}
          onError={(event: any) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    if (form.icon && typeof form.icon === 'string') {
      return (
        <Box
          component="img"
          src={form.icon}
          alt={`${form.name || product?.name || 'Product'} icon`}
          sx={{
            width: 28,
            height: 28,
            objectFit: 'contain',
            flexShrink: 0,
          }}
          onError={(event: any) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    return null;
  };

  const renderVersionControls = () => {
    if (isNew || !shouldUseVersionScopedView) return null;

    const recommended = getRecommendedVersion(productVersions, form.recommended_version);
    const hasRecommendedVersion = !!recommended?.id;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gap={1.5}
        sx={{
          width: '100%',
          mt: { xs: 1, md: 0.5 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
          justifyContent="flex-end"
          sx={{ width: '100%' }}
        >
          <Chip
            label={
              selectedCatalogVersion
                ? `Version ${getVersionLabel(selectedCatalogVersion)}`
                : "All Versions"
            }
            size="small"
            color={selectedCatalogVersion ? "primary" : "default"}
            variant={selectedCatalogVersion ? "filled" : "outlined"}
          />

          {shouldShowProductScopedActions && (
            <SnackableCourseButton
              entityType="product"
              entityId={productNumericId}
              entityName={form.name}
              disabled={isNew}
              versionScope={effectiveProductResourceScope}
            />
          )}

          {!readOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
            >
              Save
            </Button>
          )}
        </Box>

        <Box
          display="flex"
          gap={1.5}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="flex-end"
          sx={{
            width: '100%',
            pr: 0,
          }}
        >
          {hasRecommendedVersion && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleUseRecommendedVersion}
              disabled={!productVersions.length}
            >
              Use Recommended
            </Button>
          )}

          <TextField
            select
            size="small"
            label="Viewing"
            value={selectedCatalogVersionId == null ? VIEW_ALL_VERSION_VALUE : selectedCatalogVersionId}
            onChange={(e) => {
              const value = e.target.value;

              if (value === VIEW_ALL_VERSION_VALUE) {
                setSelectedCatalogVersionId(null);
                return;
              }

              const nextValue = Number(value);
              setSelectedCatalogVersionId(Number.isFinite(nextValue) ? nextValue : null);
            }}
            sx={{ minWidth: 300 }}
            disabled={!productVersions.length}
          >
            <MenuItem value={VIEW_ALL_VERSION_VALUE}>
              View All Versions
            </MenuItem>

            {productVersions.map((version: any) => (
              <MenuItem key={version.id} value={version.id}>
                {getVersionLabel(version)}
                {String(version.version ?? '').trim() === String(form.recommended_version ?? '').trim()
                  ? ' — Recommended'
                  : ''}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    );
  };

  const renderNonVersionedCourseAndSave = () => {
    if (isNew || shouldUseVersionScopedView) {
      return (
        <>
          {(loading || saving) && <CircularProgress size={20} />}

          {!readOnly && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
            >
              Save
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        <SnackableCourseButton
          entityType="product"
          entityId={productNumericId}
          entityName={form.name}
          disabled={isNew}
          versionScope={effectiveProductResourceScope}
        />

        {(loading || saving) && <CircularProgress size={20} />}

        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
          >
            Save
          </Button>
        )}
      </>
    );
  };

  return (
    <Box className="my-6">
      {(isNew || !!product) && (
        <NavHistoryTracker title={isNew ? 'New Product' : product.name} />
      )}

      {!hasProductScope ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No scope access for Products.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'auto 1fr' }}
            alignItems="flex-start"
            mb={3}
            gap={3}
          >
            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <Button startIcon={<ArrowBack />} onClick={handleClose}>
                Back
              </Button>

              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
                  {readOnly
                    ? 'View Product'
                    : isNew
                      ? 'Create Product'
                      : form?.name
                        ? `Product: ${form.name}`
                        : `Product #${productNumericId}`}
                </Typography>
                {renderProductTitleIcon()}
              </Box>
            </Box>

            <Box
              display="flex"
              flexDirection={shouldUseVersionScopedView ? 'column' : 'row'}
              gap={1.5}
              alignItems="flex-end"
              justifyContent="flex-start"
              sx={{
                justifySelf: 'end',
                width: { xs: '100%', md: shouldUseVersionScopedView ? 620 : 'auto' },
                maxWidth: '100%',
                ml: { xs: 0, md: 'auto' },
              }}
            >
              {shouldUseVersionScopedView ? renderVersionControls() : renderNonVersionedCourseAndSave()}
            </Box>
          </Box>

          {shouldShowVersionLoadingState && (
            <Alert
              severity="info"
              icon={<CircularProgress size={18} />}
              sx={{ mb: 2, alignItems: 'center' }}
            >
              Loading product versions...
            </Alert>
          )}

          {shouldShowNoVersionsWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No versions are configured for this product. Add versions before managing version-specific catalog content.
            </Alert>
          )}

          <Grid container spacing={2} sx={equalHeightGridSx}>
            <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
              <Grid container spacing={2} sx={equalHeightGridSx}>
                <Grid item xs={12} md={6} sx={equalHeightGridItemSx}>
                  <ChildCrudPanel
                    key={`product-${productNumericId}-product-components-${selectedCatalogVersionId ?? 'all'}`}
                    parentId={productNumericId}
                    sectionType={CacheKey.ProductComponent}
                    titleOverride="Components"
                    columnsOverride={nameOnlyColumns}
                    readOnly={readOnly}
                    canCreate={canCreateChildren}
                    canUpdate={canUpdateChildren}
                    canDelete={canDeleteChildren}
                    versionScoped={shouldUseVersionScopedView}
                    selectedVersionId={selectedCatalogVersionId}
                    selectedVersionLabel={selectedCatalogVersionLabel}
                    versionScope={selectedVersionScope}
                  />
                </Grid>

                <Grid item xs={12} md={6} sx={equalHeightGridItemSx}>
                  <ChildCrudPanel
                    key={`product-${productNumericId}-product-use-cases-${selectedCatalogVersionId ?? 'all'}`}
                    parentId={productNumericId}
                    sectionType={CacheKey.ProductUseCase}
                    titleOverride="Use Cases"
                    columnsOverride={nameOnlyColumns}
                    readOnly={readOnly}
                    canCreate={canCreateChildren}
                    canUpdate={canUpdateChildren}
                    canDelete={canDeleteChildren}
                    versionScoped={shouldUseVersionScopedView}
                    selectedVersionId={selectedCatalogVersionId}
                    selectedVersionLabel={selectedCatalogVersionLabel}
                    versionScope={selectedVersionScope}
                  />
                </Grid>
              </Grid>

              <Box mt={2} />

              <Box sx={fullWidthPanelSx}>
                <ChildCrudPanel
                  key={`product-${productNumericId}-features-${selectedCatalogVersionId ?? 'all'}`}
                  parentId={productNumericId}
                  sectionType={CacheKey.ProductFeature}
                  columnsOverride={productFeatureColumns}
                  readOnly={readOnly}
                  canCreate={canCreateChildren}
                  canUpdate={canUpdateChildren}
                  canDelete={canDeleteChildren}
                  versionScoped={shouldUseVersionScopedView}
                  selectedVersionId={selectedCatalogVersionId}
                  selectedVersionLabel={selectedCatalogVersionLabel}
                  versionScope={selectedVersionScope}
                />
              </Box>

              <Box mt={2} />

              {shouldUseVersionScopedView ? (
                <>
                  <Grid container spacing={2} sx={equalHeightGridSx}>
                    <Grid item xs={12} md={6} sx={equalHeightGridItemSx}>
                      <ChildCrudPanel
                        key={`product-${productNumericId}-licenses`}
                        parentId={productNumericId}
                        sectionType={CacheKey.ProductLicense}
                        readOnly={readOnly}
                        canCreate={canCreateChildren}
                        canUpdate={canUpdateChildren}
                        canDelete={false}
                      />
                    </Grid>

                    <Grid item xs={12} md={6} sx={equalHeightGridItemSx}>
                      <ChildCrudPanel
                        key={`product-${productNumericId}-appliances`}
                        parentId={productNumericId}
                        sectionType={CacheKey.ProductAppliance}
                        readOnly={readOnly}
                        canCreate={canCreateChildren}
                        canUpdate={canUpdateChildren}
                        canDelete={false}
                      />
                    </Grid>
                  </Grid>

                  <Box mt={2} />

                  <Accordion defaultExpanded sx={{ mb: 2, width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box>
                        <Typography variant="h6">
                          Versions
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manage appliance/hybrid product versions and map features, licenses, and version-scoped resources.
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 0 }}>
                      <ChildCrudPanel
                        key={`product-${productNumericId}-versions`}
                        parentId={productNumericId}
                        sectionType={CacheKey.ProductVersion}
                        readOnly={readOnly}
                        canCreate={canCreateChildren}
                        canUpdate={canUpdateChildren}
                        canDelete={false}
                      />
                    </AccordionDetails>
                  </Accordion>
                </>
              ) : (
                <>
                  <Box sx={fullWidthPanelSx}>
                    <ChildCrudPanel
                      key={`product-${productNumericId}-licenses`}
                      parentId={productNumericId}
                      sectionType={CacheKey.ProductLicense}
                      readOnly={readOnly}
                      canCreate={canCreateChildren}
                      canUpdate={canUpdateChildren}
                      canDelete={false}
                    />
                  </Box>

                  {!isSaasOffer && (
                    <>
                      <Box mt={2} />
                      <Box sx={fullWidthPanelSx}>
                        <ChildCrudPanel
                          key={`product-${productNumericId}-appliances`}
                          parentId={productNumericId}
                          sectionType={CacheKey.ProductAppliance}
                          readOnly={readOnly}
                          canCreate={canCreateChildren}
                          canUpdate={canUpdateChildren}
                          canDelete={false}
                        />
                      </Box>
                    </>
                  )}
                </>
              )}
            </Grid>

            <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6">Attributes</Typography>
                <Divider sx={{ my: 1.5 }} />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Name"
                  value={form.name ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  value={form.description ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, description: e.target.value }))}
                  multiline
                  minRows={3}
                  disabled={readOnly}
                />

                <TextField fullWidth margin="normal" label={'Vendor'} value={'Cisco'} disabled />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Sales URL"
                  value={form.sales_url ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, sales_url: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Sales Connect URL"
                  value={form.sales_connect_url ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, sales_connect_url: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Offer Type"
                  value={form.offer_type ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, offer_type: e.target.value }))}
                  disabled={readOnly}
                  helperText={
                    shouldUseVersionScopedView
                      ? "Appliance/hybrid offers support version-scoped catalog filtering."
                      : isSaasOffer
                        ? "SaaS offers show the current product catalog."
                        : undefined
                  }
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Abbreviation"
                  value={form.abbreviation ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, abbreviation: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Recommended Version"
                  value={form.recommended_version ?? ''}
                  onChange={(e) => !readOnly && setForm((p) => ({ ...p, recommended_version: e.target.value }))}
                  disabled={readOnly}
                />

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    margin="normal"
                    type="date"
                    label="Release Date"
                    InputLabelProps={{ shrink: true }}
                    value={form.release_date ?? ''}
                    onChange={(e) => !readOnly && setForm((p) => ({ ...p, release_date: e.target.value }))}
                    disabled={readOnly}
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    type="date"
                    label="End of Life Date"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_of_life_date ?? ''}
                    onChange={(e) => !readOnly && setForm((p) => ({ ...p, end_of_life_date: e.target.value }))}
                    disabled={readOnly}
                  />
                </Box>

                <ImageUploadSelectControl
                  listImagesUrl="db/svg-icon/"
                  uploadImageUrl="db/svg-icon/"
                  selectedImage={form.svg_icon == null ? '' : String(getSvgIconId(form.svg_icon) ?? '')}
                  selectImage={(value: string) => {
                    if (readOnly) return;
                    setForm((p) => ({
                      ...p,
                      svg_icon: value || null,
                    }));
                  }}
                  disabled={readOnly}
                />
              </Paper>

              <Box mt={2} />

              <Paper sx={{ p: 2 }}>
                <TagManagementSection
                  parentId={productNumericId}
                  parentType={CacheKey.Product}
                  disabled={!validId(productNumericId) || readOnly}
                />
              </Paper>

              {shouldShowProductScopedActions && (
                <>
                  <Box mt={2} />

                  <Paper sx={{ p: 2 }}>
                    <ResourceRelationPanel
                      joinEndpoint="db/product-to-resource"
                      joinCacheKey={CacheKey.ProductToResource}
                      parentField="product"
                      parentId={productNumericId}
                      title={effectiveProductResourceScope ? "Product Resources for Selected Version" : "Resources"}
                      showNameColumn={true}
                      maxHeight={400}
                      invalidateKeys={[CacheKey.Product]}
                      readOnly={readOnly as any}
                      versionScope={effectiveProductResourceScope}
                      showVersionScopeColumn={true}
                    />
                  </Paper>
                </>
              )}
            </Grid>
          </Grid>

          {courseManagerOpen && shouldShowProductScopedActions && (
            <SnackableCourseManager
              open={courseManagerOpen}
              entityType="product"
              entityId={productNumericId}
              entityName={form.name}
              versionScope={effectiveProductResourceScope}
              onClose={() => setCourseManagerOpen(false)}
            />
          )}
        </>
      )}

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this product editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </Box>
  );
}